import IconTextLink from '../../../common/IconTextLink';
import React, { type ReactElement } from 'react';
import { Box, useToast } from 'braid-design-system';
import type { IJobApplicationStore } from '../../../../stores/JobApplicationStore';
import type { IModalStore } from '../../../../stores/UiStore/ModalStore';
import { observer } from 'mobx-react';
import Modal from '../../../common/Modal';
import * as styles from './BulkActions.css';
import ContentWithListContainer from '../../../app/ContentWithListContainer';
import {
  BULK_ACTION,
  useGetBulkActionItemAttributes,
} from './hooks/useBulkActionItemInfo';
import { sendHotjarTag } from 'src/utils/hotjar';
import { HOTJAR_TAG_BULK_ACTIONS } from 'src/constants/hotjar';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import type { ToastAction } from 'src/components/common/ActionableFormView';

interface Props {
  jobApplicationStore: IJobApplicationStore;
  modalStore?: IModalStore;
  disabled: boolean;
  bulkAction: BULK_ACTION;
  icon: ReactElement;
  children: (props: BulkActionItemChildProps) => ReactElement;
}

export interface BulkActionItemChildProps {
  onCancel: () => void;
  closeAndShowSuccessMessage: (
    message: string,
    description?: string,
    action?: ToastAction,
  ) => void;
  selectedJobApplicationsCount: number;
  enableModal: () => void;
  disableModal: () => void;
}

const BulkActionItem = ({
  disabled = true,
  jobApplicationStore,
  bulkAction,
  icon,
  children,
}: Props) => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const bulkActionAttributes = useGetBulkActionItemAttributes(bulkAction);
  const bulkActionName = bulkActionAttributes.name;
  const modalStore = jobApplicationStore.rootStore.uiStore.modalStore;
  const modalName = bulkActionAttributes.modalName;

  // FIXME: deciding the limit based on the bulk actions at this component level
  // makes BulkActionItem so coupled to the these two.
  // It's much better to extract these logic into a function/hook instead.
  const selectedJobApplications =
    bulkAction === BULK_ACTION.MOVE
      ? jobApplicationStore.selectedJobApplicationUpToBulkMoveLimit
      : jobApplicationStore.selectedJobApplications;

  // FIXME: Sku's static rendering is currently rendering this component which means selectedJobApplications can be undefined
  // i.e. we need ?. operator here until we make this component client-only
  const selectedJobApplicationsCount = selectedJobApplications?.length || 0;

  const onClickItemLink = () => {
    bulkActionAttributes.trackOnClick(
      jobApplicationStore.bulkActionEventAttributes,
    );
    sendHotjarTag(HOTJAR_TAG_BULK_ACTIONS);
    return modalStore.addModal(modalName);
  };

  const removeModal = () => {
    modalStore.removeModal(modalName);
  };

  const closeAndShowSuccessMessage: BulkActionItemChildProps['closeAndShowSuccessMessage'] =
    (message, description, action) => {
      showToast({
        message,
        description,
        tone: 'positive',
        action,
        closeLabel: t('Close'),
      });
      removeModal();
    };

  const listingViewHeading =
    bulkAction === BULK_ACTION.MOVE
      ? t('Move application: labelCount', {
          labelCount: selectedJobApplicationsCount,
        })
      : t('Email candidate: labelCount', {
          labelCount: selectedJobApplicationsCount,
        });

  const classStyle = `${styles.modal} ${
    bulkAction === BULK_ACTION.MOVE
      ? styles.move
      : bulkAction === BULK_ACTION.EMAIL ?? styles.email
  }`;

  return (
    <Box paddingX="small">
      <IconTextLink
        disabled={disabled}
        icon={icon}
        label={bulkActionAttributes.itemLinkLabel}
        onClick={onClickItemLink}
        tooltipText={bulkActionAttributes.tooltipText}
        testId={`bulk-${bulkActionName}-action`}
      />
      <Modal
        modalStore={modalStore}
        name={modalName}
        contentClassName={classStyle}
      >
        {({ enableModal, disableModal }) => (
          <div
            data-testid={`bulk-${bulkActionName}-modal`}
            className={styles.bulkModal}
          >
            <ContentWithListContainer
              listViewHeading={listingViewHeading}
              selectedJobApplications={selectedJobApplications}
            >
              {children({
                onCancel: removeModal,
                closeAndShowSuccessMessage,
                selectedJobApplicationsCount,
                enableModal,
                disableModal,
              })}
            </ContentWithListContainer>
          </div>
        )}
      </Modal>
    </Box>
  );
};

export default observer(BulkActionItem);
